import apiDictionary from '@/api/modules/dictionary'

export interface DictionaryItem {
  name: string
  value: string
  color?: string
  icon?: string
}

export const useDictionaryStore = defineStore(
  'dictionary',
  () => {
    const isInit = ref(false)
    const dictionary = ref<Record<string, DictionaryItem[]>>({})
    const loadingPromise = shallowRef<Promise<void> | null>(null)

    /**
     * 将标准 options 数据映射成字典条目。
     */
    function mapOptionsToDictionary(items: unknown = []) {
      const normalized = Array.isArray(items)
        ? items
        : Array.isArray((items as { data?: unknown[] })?.data)
          ? (items as { data: unknown[] }).data
          : []

      return normalized.map((item: any) => ({
        value: String(item.value),
        name: String(item.label),
        ...(item.color ? { color: String(item.color) } : {}),
        ...(item.icon ? { icon: String(item.icon) } : {}),
      }))
    }

    /**
     * 用后端统一入口刷新后台字典。
     */
    async function fetchAndAssignDictionaries() {
      const res = await apiDictionary.dictionaryBootstrap()
      const bootstrap = res.data ?? {}
      const nextDictionary: Record<string, DictionaryItem[]> = {}

      Object.entries(bootstrap).forEach(([code, items]) => {
        nextDictionary[code] = mapOptionsToDictionary(items)
      })

      dictionary.value = nextDictionary
      isInit.value = true
    }

    /**
     * 初始化字典。
     *
     * 说明：
     * - 只认独立字典模块，不再依赖 app.ts
     * - 并发场景下复用同一请求，避免重复初始化
     */
    async function init(force = false) {
      if (force) {
        dictionary.value = {}
        isInit.value = false
        loadingPromise.value = null
      }

      if (isInit.value) {
        return
      }

      if (loadingPromise.value === null) {
        loadingPromise.value = fetchAndAssignDictionaries()
          .finally(() => {
            loadingPromise.value = null
          })
      }

      await loadingPromise.value
    }

    async function get(code: string) {
      if (dictionary.value[code] === undefined) {
        await init()
      }
      return dictionary.value[code] ?? []
    }

    function getName(code: string, value: string) {
      const list = dictionary.value[code]
      if (list) {
        return list.find(item => item.value === value)?.name
      }
      return undefined
    }

    function getValue(code: string, name: string) {
      const list = dictionary.value[code]
      if (list) {
        return list.find(item => item.name === name)?.value
      }
      return undefined
    }

    /**
     * 清空当前登录态下缓存的字典数据。
     */
    function clear() {
      dictionary.value = {}
      isInit.value = false
      loadingPromise.value = null
    }

    return {
      init,
      clear,
      get,
      getName,
      getValue,
    }
  },
)
